import { Link, router, usePage } from '@inertiajs/react'
import { useState, useRef } from 'react'
import { CartProvider, Cart } from '../../lib/cart'
import { formatLempiras } from '../../lib/format'
import CartHeader from '../../lib/CartHeader'

interface Account { id: number; name: string; slug: string; logo_url?: string | null }
interface OrderItem { id: number; product_name: string; variant_name?: string; quantity: number; line_total_cents: number }
interface Order {
  id: number
  subtotal_cents: number
  tax_cents: number
  total_cents: number
  payment_method: string
  state: string
  reference_number?: string
  payment_proof_url?: string | null
  order_items: OrderItem[]
}
interface BankAccount { bank_name: string; account_number: string; account_holder: string; currency: string }
interface Props { account: Account; order: Order; bank_accounts: BankAccount[]; cart?: Cart; signup_pending?: boolean; signup_email?: string | null; flash?: { notice?: string; alert?: string } }

export default function CatalogThanks(props: Props) {
  const { account, cart } = props
  return (
    <CartProvider cart={cart} slug={account.slug}>
      <CatalogThanksInner {...props} />
    </CartProvider>
  )
}

function CatalogThanksInner({ account, order, bank_accounts, signup_pending, signup_email }: { account: Account; order: Order; bank_accounts: BankAccount[]; signup_pending?: boolean; signup_email?: string | null; flash?: { notice?: string; alert?: string } }) {
  const { flash } = usePage().props as { flash?: { notice?: string; alert?: string } }
  const [processing, setProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const stateLabel: Record<string, string> = {
    awaiting_payment: 'Esperando pago',
    confirming: 'Pago en verificación',
    paid: 'Pagado',
    ready_for_pickup: 'Listo para recoger',
    shipped: 'Enviado',
    completed: 'Completado',
    cancelled: 'Cancelado',
  }

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault()
    const file = fileInputRef.current?.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('payment_proof', file)

    setProcessing(true)
    router.post(`/${account.slug}/catalog/thanks/${order.reference_number}/proof`, formData, {
      preserveScroll: true,
      onSuccess: () => setProcessing(false),
      onError: () => setProcessing(false),
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />

      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        {/* Flash messages */}
        {flash?.notice && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded mb-4 text-sm">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded mb-4 text-sm">
            {flash.alert}
          </div>
        )}

        <div className="text-6xl mb-4">✅</div>
        <h1 className="text-3xl font-bold text-[#202020] mb-2">¡Pedido registrado!</h1>
        <p className="text-gray-600 mb-2">
          Tu pedido <strong className="font-mono">{order.reference_number}</strong> está <strong>{stateLabel[order.state] || order.state}</strong>.
        </p>
        {order.reference_number && (
          <p className="text-sm text-gray-500 mb-6">
            Número de referencia: <strong className="text-[#202020] font-mono text-lg">{order.reference_number}</strong>
          </p>
        )}

        {/* Order summary */}
        <div className="bg-white rounded-lg shadow p-6 text-left mb-6">
          <h2 className="font-semibold text-[#202020] mb-3">Resumen del pedido</h2>
          {order.order_items.map((i) => (
            <div key={i.id} className="flex justify-between py-1 text-sm">
              <span>
                {i.quantity}× {i.product_name}
                {i.variant_name && ` (${i.variant_name})`}
              </span>
              <span>{formatLempiras(i.line_total_cents)}</span>
            </div>
          ))}
          <div className="border-t mt-3 pt-3 space-y-1">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatLempiras(order.subtotal_cents)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>ISV (15%)</span>
              <span>{formatLempiras(order.tax_cents)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span>{formatLempiras(order.total_cents)}</span>
            </div>
          </div>
        </div>

        {/* Bank accounts for transfer */}
        {order.payment_method === 'transfer' && bank_accounts.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-left mb-6">
            <p className="font-semibold text-[#202020] mb-2">💰 Datos para transferencia</p>
            {order.reference_number && (
              <p className="text-sm text-gray-700 mb-3">
                Usa como concepto: <strong className="font-mono bg-yellow-100 px-1 rounded">{order.reference_number}</strong>
              </p>
            )}
            <div className="space-y-2">
              {bank_accounts.map((ba, i) => (
                <div key={i} className="bg-white rounded border p-3 text-sm">
                  <p><strong>{ba.bank_name}</strong> — {ba.currency}</p>
                  <p className="text-gray-700">Cuenta: <span className="font-mono">{ba.account_number}</span></p>
                  <p className="text-gray-700">Titular: {ba.account_holder}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment proof upload — only for transfer and when state allows */}
        {order.payment_method === 'transfer' && (order.state === 'awaiting_payment' || order.state === 'confirming') && (
          <div className="bg-blue-50 border border-blue-200 rounded p-4 text-left mb-6">
            <p className="font-semibold text-[#202020] mb-2">📎 Subir comprobante de pago</p>
            {order.payment_proof_url ? (
              <div className="space-y-2">
                <p className="text-sm text-green-700">✅ Comprobante subido. Estamos verificando tu pago.</p>
                <a href={order.payment_proof_url} target="_blank" rel="noopener noreferrer"
                  className="text-[#55ACEE] hover:underline text-sm">Ver comprobante</a>
              </div>
            ) : (
              <form onSubmit={handleUpload} className="space-y-3">
                <input type="file" accept="image/*,.pdf" ref={fileInputRef}
                  className="w-full text-sm text-gray-700" required />
                <button type="submit" disabled={processing}
                  className="bg-[#55ACEE] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#3a8bc4] disabled:opacity-50">
                  {processing ? 'Subiendo...' : 'Subir comprobante'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Aviso de cuenta creada: el invitado recibió el enlace para poner su contraseña */}
        {signup_pending && (
          <div className="bg-amber-50 border border-[#FFC107] rounded p-4 text-left mb-6">
            <p className="font-semibold text-[#202020] mb-1">📧 Revisa tu correo</p>
            <p className="text-sm text-gray-700">
              Te enviamos un enlace {signup_email && <strong>a {signup_email} </strong>}
              para crear tu contraseña. Con ella verás todos tus pedidos y no tendrás que
              volver a escribir tus datos.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              ¿No lo encuentras? Revisa la carpeta de spam o{' '}
              <Link href={`/${account.slug}/portal/forgot_password`} className="text-[#55ACEE] hover:underline">
                pide un enlace nuevo
              </Link>.
            </p>
          </div>
        )}

        {/* Link de Pago message */}
        {order.payment_method === 'link_request' && (
          <div className="bg-blue-50 border border-blue-200 rounded p-4 text-left mb-6">
            <p className="font-semibold text-[#202020] mb-2">🔗 Link de Pago</p>
            <p className="text-sm text-gray-700">
              El comercio te enviará un link de pago a tu email en breve.
            </p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <Link
            href={`/${account.slug}/catalog`}
            className="bg-[#55ACEE] text-white px-6 py-2 rounded font-semibold hover:bg-[#3a8bc4]"
          >
            Seguir comprando
          </Link>
          <Link
            href={`/${account.slug}/portal/orders`}
            className="bg-white text-[#202020] px-6 py-2 rounded font-semibold border hover:bg-gray-50"
          >
            Ver mis pedidos
          </Link>
        </div>
      </div>
    </div>
  )
}
